import { useAuth, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { PaginatedList } from "@tellescope/ui";
import { useMemo } from "react";
import { useInboxSearchParams } from "../../hooks";
import { getInboxView } from "../../utils";
import { InboxThreadListDefaultItem } from "./list-items";
import { InboxThreadAutoSelect } from "./InboxThreadAutoSelect";

/**
 * Component for rendering the inbox thread list
 * Isolated for better performance - only re-renders when threads change
 */
export const InboxThreadList = () => {
  const location = useLocation();
  const view = getInboxView(location);
  const { currentUser } = useAuth();
  const [inboxQueryParams] = useInboxSearchParams();
  const mdbFilter = useMemo<object | undefined>(() => {
    const filter: Record<string, unknown> = {};
    switch (view) {
      case "inbox":
        filter.archivedAt = "";
        filter.trashedAt = "";
        break;
      case "archive":
        filter.archivedAt = {
          $ne: "",
        };
        filter.trashedAt = "";
        break;
      case "trash":
        filter.trashedAt = {
          $ne: "",
        };
        filter.archivedAt = "";
        break;
    }

    if (currentUser.id) {
      switch (inboxQueryParams.readStatus) {
        case "unread":
          filter[`readBy.${currentUser.id}`] = {
            $in: [null, ""],
          };
          break;
        case "read":
          filter[`readBy.${currentUser.id}`] = {
            $nin: [null, ""],
          };
          break;
      }
    }

    if (inboxQueryParams.inboxStatus?.length) {
      filter.inboxStatus = {
        $in: inboxQueryParams.inboxStatus,
      };
    }

    if (inboxQueryParams.channels?.length) {
      filter.type = {
        $in: inboxQueryParams.channels,
      };
    } else {
      filter.type = {
        $nin: ["Phone", "GroupMMS"],
      };
    }

    if (inboxQueryParams.tags?.length) {
      filter.tags = {
        $elemMatch: {
          $in: inboxQueryParams.tags,
        },
      };
    }

    if (inboxQueryParams.dateRange?.from) {
      filter.timestamp = {
        $gte: new Date(inboxQueryParams.dateRange.from),
      };
    }

    if (inboxQueryParams.dateRange?.to) {
      filter.timestamp = {
        ...(typeof filter.timestamp === "object" && filter.timestamp !== null ? filter.timestamp : {}),
        $lte: new Date(inboxQueryParams.dateRange.to),
      };
    }

    if (inboxQueryParams.scheduled) {
      filter.timestamp = {
        $gt: new Date(),
      };
    } else if (inboxQueryParams.scheduled === false && !filter.timestamp) {
      filter.timestamp = {
        $lte: new Date(),
      };
    }

    if (inboxQueryParams.assignees?.length) {
      filter.assignedTo = {
        $elemMatch: {
          $in: inboxQueryParams.assignees,
        },
      };
    }

    if (inboxQueryParams.patients?.length) {
      filter.enduserIds = {
        $elemMatch: {
          $in: inboxQueryParams.patients,
        },
      };
    }

    return filter;
  }, [
    currentUser.id,
    inboxQueryParams.assignees,
    inboxQueryParams.channels,
    inboxQueryParams.dateRange?.from,
    inboxQueryParams.dateRange?.to,
    inboxQueryParams.inboxStatus,
    inboxQueryParams.patients,
    inboxQueryParams.readStatus,
    inboxQueryParams.scheduled,
    inboxQueryParams.tags,
    view,
  ]);
  const { t } = useTranslation("inbox");
  const selectedThreadId = useParams().threadId;

  const threadsQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      limit: inboxQueryParams.search ? undefined : 50,
      lastTimestamp: inboxQueryParams.dateRange?.to ? new Date(inboxQueryParams.dateRange.to) : undefined,
      mdbFilter,
      search: inboxQueryParams.search || undefined,
    },
    getNextPageParam: (lastPage) => lastPage.threads[lastPage.threads.length - 1]?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) =>
      data.pages
        .flatMap((page) => page.threads)
        .sort((a, b) => {
          // The BE sort is unreliable, we need to correct the sort to be the latest of the two timestamps
          const aLatest =
            a.outboundTimestamp && a.outboundTimestamp.getTime() >= a.timestamp.getTime()
              ? a.outboundTimestamp
              : a.timestamp;
          const bLatest =
            b.outboundTimestamp && b.outboundTimestamp.getTime() >= b.timestamp.getTime()
              ? b.outboundTimestamp
              : b.timestamp;

          return bLatest.getTime() - aLatest.getTime();
        }),
  });

  if (threadsQuery.isError) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <div className="text-red-500 mb-2">{t("Error loading inbox threads")}</div>
      </div>
    );
  }

  if (threadsQuery.isLoading) {
    return <div className="p-4 text-center text-muted-foreground">{t("Loading messages...")}</div>;
  }

  if (threadsQuery.data?.length === 0) {
    return <div className="p-4 text-center text-muted-foreground">{t("No messages found")}</div>;
  }

  return (
    <>
      <InboxThreadAutoSelect threads={threadsQuery.data} />
      <PaginatedList
        className="px-4 pb-4 space-y-2 flex-1"
        onBottomReached={() => {
          threadsQuery.fetchNextPage();
        }}
      >
        {threadsQuery.data?.map((thread) => (
          <InboxThreadListDefaultItem
            key={thread.id}
            thread={thread}
            isSelected={selectedThreadId === thread.id}
            className="my-1"
          />
        ))}
      </PaginatedList>
    </>
  );
};

InboxThreadList.displayName = "InboxThreadList";
